"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Phone, PhoneOff } from "lucide-react";

import { VideoCallOverlay } from "@/components/chat/video-call-overlay";
import { Button } from "@/components/ui/button";
import { useCurrentUser } from "@/hooks/use-current-user";
import { useNativeApp } from "@/hooks/use-native-app";
import { isNativeApp } from "@/lib/native/platform";
import {
  endVideoCall,
  joinVideoCall,
  listLiveConversationCalls,
  startVideoCall,
  type VideoCallSession,
} from "@/lib/chat/video-call";
import type { ConversationCall } from "@/lib/chat/types";
import { getSupabaseBrowserClient } from "@/lib/supabase/client";
import { getErrorMessage } from "@/lib/utils";

type VideoCallContextValue = {
  startCall: (conversationId: string, title: string) => Promise<void>;
  joinCall: (call: ConversationCall, title: string) => Promise<void>;
  liveCallFor: (conversationId: string) => ConversationCall | null;
  pending: boolean;
  error: string | null;
};

const VideoCallContext = createContext<VideoCallContextValue | null>(null);

export function useVideoCall() {
  const value = useContext(VideoCallContext);
  if (!value) {
    throw new Error("useVideoCall a VideoCallProvideren belül használható.");
  }
  return value;
}

const disabledVideoCallValue: VideoCallContextValue = {
  startCall: async () => undefined,
  joinCall: async () => undefined,
  liveCallFor: () => null,
  pending: false,
  error: null,
};

export function VideoCallProvider({ children }: { children: React.ReactNode }) {
  const isNative = useNativeApp();
  const currentUserQuery = useCurrentUser();
  const userId = currentUserQuery.data?.id ?? null;
  const [liveCalls, setLiveCalls] = useState<ConversationCall[]>([]);
  const [session, setSession] = useState<(VideoCallSession & { title: string }) | null>(
    null,
  );
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const incoming = liveCalls.find(
    (call) =>
      call.status === "ringing" &&
      userId &&
      call.createdBy !== userId &&
      session?.callId !== call.id,
  );

  const refreshLive = useCallback(async () => {
    if (!userId) {
      setLiveCalls([]);
      return;
    }
    const calls = await listLiveConversationCalls();
    setLiveCalls(calls);
  }, [userId]);

  useEffect(() => {
    if (isNativeApp()) {
      return;
    }
    void refreshLive().catch(() => undefined);
  }, [refreshLive]);

  useEffect(() => {
    if (isNativeApp() || !userId) {
      return;
    }
    const supabase = getSupabaseBrowserClient();
    const channel = supabase
      .channel("conversation-calls")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "conversation_calls" },
        () => {
          void refreshLive().catch(() => undefined);
        },
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [refreshLive, userId]);

  useEffect(() => {
    if (!session) {
      return;
    }
    const live = liveCalls.find((call) => call.id === session.callId);
    if (live && live.status === "ended") {
      setSession(null);
    }
  }, [liveCalls, session]);

  const hangUp = useCallback(async () => {
    const current = session;
    setSession(null);
    if (!current) {
      return;
    }
    try {
      await endVideoCall({
        conversationId: current.conversationId,
        callId: current.callId,
      });
    } catch {
      // A helyi overlay akkor is bezárul, ha a szerver már lezárta a hívást.
    }
    void refreshLive().catch(() => undefined);
  }, [refreshLive, session]);

  const startCall = useCallback(
    async (conversationId: string, title: string) => {
      if (session?.conversationId === conversationId) {
        return;
      }
      setPending(true);
      setError(null);
      try {
        const next = await startVideoCall(conversationId);
        setSession({ ...next, title });
        await refreshLive();
      } catch (cause) {
        setError(getErrorMessage(cause));
        throw cause;
      } finally {
        setPending(false);
      }
    },
    [refreshLive, session],
  );

  const joinCall = useCallback(
    async (call: ConversationCall, title: string) => {
      if (session?.callId === call.id) {
        return;
      }
      setPending(true);
      setError(null);
      try {
        const next = await joinVideoCall({
          conversationId: call.conversationId,
          callId: call.id,
        });
        setSession({ ...next, title });
        await refreshLive();
      } catch (cause) {
        setError(getErrorMessage(cause));
        throw cause;
      } finally {
        setPending(false);
      }
    },
    [refreshLive, session],
  );

  const value = useMemo<VideoCallContextValue>(
    () =>
      isNative
        ? disabledVideoCallValue
        : {
            startCall,
            joinCall,
            liveCallFor: (conversationId: string) =>
              liveCalls.find(
                (call) =>
                  call.conversationId === conversationId && call.status !== "ended",
              ) ?? null,
            pending,
            error,
          },
    [error, isNative, joinCall, liveCalls, pending, startCall],
  );

  return (
    <VideoCallContext.Provider value={value}>
      {children}
      {!isNative && incoming && !session ? (
        <div className="fixed inset-x-0 top-16 z-[70] flex justify-center px-4">
          <div className="flex w-full max-w-lg items-center gap-3 rounded-xl border bg-card px-4 py-3">
            <Phone className="size-5 text-primary" />
            <p className="min-w-0 flex-1 text-sm font-medium">Bejövő videóhívás</p>
            <Button
              type="button"
              size="sm"
              disabled={pending}
              onClick={() => void joinCall(incoming, "Videóhívás")}
            >
              Fogadás
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={pending}
              onClick={() =>
                void endVideoCall({
                  conversationId: incoming.conversationId,
                  callId: incoming.id,
                }).then(() => refreshLive())
              }
            >
              <PhoneOff className="size-4" />
              Elutasítás
            </Button>
          </div>
        </div>
      ) : null}
      {!isNative && session ? (
        <VideoCallOverlay
          roomUrl={session.roomUrl}
          token={session.token}
          title={session.title}
          onLeave={() => void hangUp()}
        />
      ) : null}
    </VideoCallContext.Provider>
  );
}
